import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError, fetchHygieneChecksConfig, patchHygieneChecksConfig } from '@/api/client'
import type { GenericHygieneCheck } from '@/api/types'

import { SlidersHorizontal } from 'lucide-react'
import { AdminSection } from '@/features/admin/AdminSection'

export function HygieneAdminPage() {
  const qc = useQueryClient()
  const configQuery = useQuery({
    queryKey: ['config', 'hygiene-checks'],
    queryFn: fetchHygieneChecksConfig,
  })
  const [generic, setGeneric] = useState<GenericHygieneCheck[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (configQuery.data?.generic_checks) {
      setGeneric(configQuery.data.generic_checks)
    }
  }, [configQuery.data])

  const saveMut = useMutation({
    mutationFn: () => patchHygieneChecksConfig({ generic }),
    onSuccess: async () => {
      setError(null)
      await qc.invalidateQueries({ queryKey: ['config', 'hygiene-checks'] })
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Failed to save hygiene checks')
    },
  })

  const seeded = configQuery.data?.seeded_checks ?? []

  return (
    <AdminSection
      title="Hygiene checks"
      tone="amber"
      icon={SlidersHorizontal}
      lede="Workbook checks (§5.6) run in fixed order; pattern-based required/forbidden fields stay derived from revenue rules. Configure generic threshold checks below."
    >
      {error && (
        <p className="alert alert--error" role="alert">
          {error}
        </p>
      )}

      <h3 className="admin-form__title">Seeded checks (read-only order)</h3>
      <div className="table-wrap">
        <table className="table table--dense">
          <thead>
            <tr>
              <th>#</th>
              <th>Code</th>
              <th>Label</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {seeded.map((row) => (
              <tr key={row.code}>
                <td>{row.order}</td>
                <td>
                  <code>{row.code}</code>
                </td>
                <td>{row.label}</td>
                <td>{row.pattern_derived ? 'Revenue rules' : 'Workbook'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="admin-form__title">Generic checks</h3>
      <div className="table-wrap">
        <table className="table table--dense">
          <thead>
            <tr>
              <th>Enabled</th>
              <th>Label</th>
              <th>Code</th>
              <th>Kind</th>
              <th>Threshold</th>
              <th>Sort</th>
            </tr>
          </thead>
          <tbody>
            {generic.map((row, index) => (
              <tr key={row.code}>
                <td>
                  <input
                    type="checkbox"
                    checked={row.enabled}
                    onChange={(e) =>
                      setGeneric((prev) =>
                        prev.map((g, i) => (i === index ? { ...g, enabled: e.target.checked } : g)),
                      )
                    }
                  />
                </td>
                <td>{row.label}</td>
                <td>
                  <code>{row.code}</code>
                </td>
                <td>{row.kind}</td>
                <td>
                  <input
                    className="field__input"
                    type="number"
                    min={0}
                    value={row.threshold}
                    onChange={(e) =>
                      setGeneric((prev) =>
                        prev.map((g, i) =>
                          i === index ? { ...g, threshold: Number(e.target.value) } : g,
                        ),
                      )
                    }
                  />
                </td>
                <td>
                  <input
                    className="field__input"
                    type="number"
                    value={row.sort_order}
                    onChange={(e) =>
                      setGeneric((prev) =>
                        prev.map((g, i) =>
                          i === index ? { ...g, sort_order: Number(e.target.value) } : g,
                        ),
                      )
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="admin-form__actions">
        <button
          className="button button--primary"
          type="button"
          disabled={saveMut.isPending}
          onClick={() => saveMut.mutate()}
        >
          Save generic checks
        </button>
      </div>
    </AdminSection>
  )
}
